import { useState, useMemo } from 'react';
import { Task, LogEntry, ProjectMetadata } from '../types';
import { useProjectStateAndStorage } from './project/useProjectStateAndStorage';
import { useTaskOperations } from './project/useTaskOperations';
import { useProjectTemplates } from './project/useProjectTemplates';
import { useProjectCollaboration } from './project/useProjectCollaboration';
import { useProjectExports } from './project/useProjectExports';
import { buildClientAllProjects } from './project/normalizeClientProjects';
import {
  useProjectHistoryAndAiActions,
  useProjectAiGenerators,
} from './project/useProjectHistoryAndAiActions';

export const useProjectManager = (userId?: string) => {
  // 1. View & Editor Reactivity States
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projectName, setProjectName] = useState('');
  const [projectMetadata, setProjectMetadata] = useState<ProjectMetadata>({});
  const [changelog, setChangelog] = useState<LogEntry[]>([]);

  // 2. Storage & Firestore Sync Domain Hook
  const storage = useProjectStateAndStorage({
    userId,
    tasks,
    setTasks,
    setProjectName,
    setProjectMetadata,
    setChangelog,
  });

  const {
    allProjects,
    setAllProjects,
    currentProjectId,
    setCurrentProjectId,
    activeProject,
    projects,
    activeWorkspace,
    setActiveWorkspace,
    isSyncing,
    setIsSyncing,
    syncError,
    setSyncError,
    lastSynced,
    setLastSynced,
    isOnline,
    pendingSyncCount,
    isInitialLoadComplete,
    user,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,
    syncTracker,
    allProjectsRef,
    currentProjectIdRef,
    migratedProjectIdsRef,
    markProjectDirty,
    markTaskDirty,
    syncProjectToFirestore,
    deleteProjectFromPersistence,
    syncInboxForce,
    restoreProjectVersion,
    reorderProjects,
    addProject,
    archiveProject,
    unarchiveProject,
    confirmDuplicateProject,
    deleteProject,
    renameProject,
    getProjectTasksWithCommVorgaenge,
  } = storage;

  // 3. Unified Global History & Command Manager (Undo / Redo / Snapshots / JSON Import)
  const {
    addLogEntry,
    commandState,
    history,
    setHistory,
    saveSnapshot,
    undo,
    redo,
    importFromJSON,
  } = useProjectHistoryAndAiActions({
    currentProjectId,
    allProjectsRef,
    syncTracker,
    setAllProjects,
    setTasks,
    setProjectName,
    setProjectMetadata,
    setChangelog,
    markProjectDirty,
    markTaskDirty,
    syncProjectToFirestore,
    getProjectTasksWithCommVorgaenge,
  });

  // 4. Task Operations Domain Hook
  const {
    commitTasks,
    updateTask,
    updateTasksMultiple,
    deleteTask,
    handleSaveTask,
    addTask,
    upgradeIdeaToTask,
    duplicateTask,
    batchUpdateTaskPositions,
    sortTasksByDate,
    handleAddAITasks,
    migrateProjectToSortOrder,
    updateProjectMetadata,
    updateProjectCategory,
    reorderTask,
  } = useTaskOperations({
    allProjects,
    syncTracker,
    allProjectsRef,
    migratedProjectIdsRef,
    setAllProjects,
    currentProjectId,
    currentProjectIdRef,
    tasks,
    setTasks,
    setProjectMetadata,
    saveSnapshot,
    addLogEntry,
    markProjectDirty,
    markTaskDirty,
    syncProjectToFirestore,
    user,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,
    getProjectTasksWithCommVorgaenge,
  });

  // 5. AI Report & Concept Generation
  const {
    isGeneratingReport,
    isGeneratingConcept,
    handleGenerateReport,
    handleGenerateConcept,
  } = useProjectAiGenerators({
    currentProjectId,
    projectName,
    projectMetadata,
    setProjectMetadata,
    updateProjectMetadata,
    addLogEntry,
  });

  // 6. Templates Domain Hook
  const { deleteSampleData, updateSampleProjectForBranch } = useProjectTemplates({
    allProjects,
    setAllProjects,
    allProjectsRef,
    currentProjectIdRef,
    setCurrentProjectId,
    userId,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    deleteProjectFromPersistence,
  });

  // 7. Collaboration & Sharing Domain Hook
  const {
    shareProjectWithUser,
    unshareProjectWithUser,
    updateProjectShareRole,
    publishProjectToLibrary,
    importProjectFromLibrary,
    deleteProjectFromLibrary,
    fetchLibraryProjects,
  } = useProjectCollaboration({
    allProjects,
    setAllProjects,
    addLogEntry,
    user,
    userId,
    syncProjectToFirestore,
    setCurrentProjectId,
  });

  // 8. Exports Domain Hook
  const {
    exportAsJSON,
    exportAsCSV,
    exportAsExcel,
    exportTaskTableAsExcel,
    exportKanbanColumnAsExcel,
    exportContactsAsExcel,
    exportAsWord,
    handleDownloadPDF,
    handleDownloadRoadmap,
  } = useProjectExports({
    activeProject,
    allProjects,
    projectName,
    projectMetadata,
    tasks,
    addLogEntry,
  });

  // 9. Client Projects Memoization
  const clientAllProjects = useMemo(
    () => buildClientAllProjects(allProjects, INBOX_PROJECT_ID, COMM_PROJECT_ID),
    [allProjects, INBOX_PROJECT_ID, COMM_PROJECT_ID]
  );

  const canUndoVal = commandState.canUndo || history.past.length > 0;
  const canRedoVal = commandState.canRedo || history.future.length > 0;

  // 10. Unified Memoized Facade Return Object (100% Signature & Reference Stability)
  return useMemo(
    () => ({
      userId,
      user,
      projects,
      allProjects: clientAllProjects,
      setAllProjects,
      currentProjectId,
      setCurrentProjectId,
      activeProject,
      tasks,
      setTasks,
      projectName,
      setProjectName,
      projectMetadata,
      setProjectMetadata,
      changelog,
      setChangelog,
      history,
      setHistory,
      isSyncing,
      setIsSyncing,
      syncError,
      setSyncError,
      lastSynced,
      setLastSynced,
      isGeneratingReport,
      isGeneratingConcept,
      saveSnapshot,
      handleGenerateReport,
      handleGenerateConcept,
      commitTasks,
      undo,
      redo,
      canUndo: canUndoVal,
      canRedo: canRedoVal,
      commandState,
      addLogEntry,
      updateTask,
      updateTasksMultiple,
      updateProjectMetadata,
      deleteTask,
      handleSaveTask,
      addTask,
      upgradeIdeaToTask,
      duplicateTask,
      batchUpdateTaskPositions,
      sortTasksByDate,
      handleAddAITasks,
      reorderTask,
      migrateProjectToSortOrder,
      addProject,
      reorderProjects,
      archiveProject,
      unarchiveProject,
      deleteProject,
      deleteSampleData,
      updateSampleProjectForBranch,
      renameProject,
      confirmDuplicateProject,
      exportAsJSON,
      exportAsCSV,
      handleDownloadPDF,
      handleDownloadRoadmap,
      exportAsWord,
      exportAsExcel,
      exportTaskTableAsExcel,
      exportKanbanColumnAsExcel,
      exportContactsAsExcel,
      importFromJSON,
      activeWorkspace,
      setActiveWorkspace,
      syncProjectToFirestore,
      restoreProjectVersion,
      syncInboxForce,
      updateProjectCategory,
      markTaskDirty,
      isOnline,
      pendingSyncCount,
      isInitialLoadComplete,
      publishProjectToLibrary,
      importProjectFromLibrary,
      deleteProjectFromLibrary,
      fetchLibraryProjects,
      shareProjectWithUser,
      unshareProjectWithUser,
      updateProjectShareRole,
    }),
    [
      userId,
      user,
      projects,
      clientAllProjects,
      setAllProjects,
      currentProjectId,
      setCurrentProjectId,
      activeProject,
      tasks,
      projectName,
      projectMetadata,
      changelog,
      history,
      setHistory,
      isSyncing,
      setIsSyncing,
      syncError,
      setSyncError,
      lastSynced,
      setLastSynced,
      isGeneratingReport,
      isGeneratingConcept,
      saveSnapshot,
      handleGenerateReport,
      handleGenerateConcept,
      commitTasks,
      undo,
      redo,
      canUndoVal,
      canRedoVal,
      commandState,
      addLogEntry,
      updateTask,
      updateTasksMultiple,
      updateProjectMetadata,
      deleteTask,
      handleSaveTask,
      addTask,
      upgradeIdeaToTask,
      duplicateTask,
      batchUpdateTaskPositions,
      sortTasksByDate,
      handleAddAITasks,
      reorderTask,
      migrateProjectToSortOrder,
      addProject,
      reorderProjects,
      archiveProject,
      unarchiveProject,
      deleteProject,
      deleteSampleData,
      updateSampleProjectForBranch,
      renameProject,
      confirmDuplicateProject,
      exportAsJSON,
      exportAsCSV,
      handleDownloadPDF,
      handleDownloadRoadmap,
      exportAsWord,
      exportAsExcel,
      exportTaskTableAsExcel,
      exportKanbanColumnAsExcel,
      exportContactsAsExcel,
      importFromJSON,
      activeWorkspace,
      setActiveWorkspace,
      syncProjectToFirestore,
      restoreProjectVersion,
      syncInboxForce,
      updateProjectCategory,
      markTaskDirty,
      isOnline,
      pendingSyncCount,
      isInitialLoadComplete,
      publishProjectToLibrary,
      importProjectFromLibrary,
      deleteProjectFromLibrary,
      fetchLibraryProjects,
      shareProjectWithUser,
      unshareProjectWithUser,
      updateProjectShareRole,
    ]
  );
};
